/**
 * Connects the analytics module to the running app: one `page_view` per
 * navigation, and how long the app took to load.
 *
 * Lives in its own service rather than in `AppComponent` so the wiring cannot
 * be lost in a revert or a merge without a test noticing — at which point the
 * whole analytics module would go quiet with nothing failing to say so.
 *
 * Registered as an app initializer, so the subscriptions exist before the router
 * performs its first navigation and no page view is missed on cold start.
 *
 * There is deliberately no identity here. The SSO never learns an account id on
 * the client, and the address a person types is exactly what must not be sent.
 */
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { trackEvent, trackPageView } from './analytics';
import { deepestRouteTitle } from './page-title.strategy';

@Injectable({ providedIn: 'root' })
export class AnalyticsBootstrapService {
  private readonly router = inject(Router);

  private started = false;

  /**
   * Idempotent, so a second registration cannot double every page view.
   *
   * Every branch is wrapped: this runs as an app initializer, so an exception
   * here would stop the whole application from starting. Analytics is never
   * worth a sign-in page that does not render.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    // Prepares the queue only. The tag itself is fetched lazily, on the first
    // tracked event.
    this.guard(() => this.trackNavigations());
    this.guard(() => this.trackLoadTime());
  }

  /** Runs a step, swallowing anything it throws. */
  private guard(step: () => void): void {
    try {
      step();
    } catch {
      // An analytics failure must stay invisible to the user and to the app.
    }
  }

  /**
   * How long the app took to become usable. GA collects no page-timing of its
   * own, so without this "app load time" simply does not exist in the reports.
   */
  private trackLoadTime(): void {
    // Not every browser exposes the navigation timing entries, and a missing
    // load time is not a reason to fail.
    if (typeof performance?.getEntriesByType !== 'function') return;

    const send = () => {
      this.guard(() => {
        const [entry] = performance.getEntriesByType('navigation') ?? [];
        const navigation = entry as PerformanceNavigationTiming | undefined;
        if (!navigation?.loadEventEnd) return;

        trackEvent('app_load', {
          load_ms: Math.round(navigation.loadEventEnd),
          nav_type: navigation.type,
        });
      });
    };

    if (document.readyState === 'complete') {
      send();
    } else {
      window.addEventListener('load', send, { once: true });
    }
  }

  private trackNavigations(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.guard(() => {
          // The title comes from the route rather than `document.title`, which
          // the router may not have updated yet at this point in the navigation.
          trackPageView(
            event.urlAfterRedirects,
            deepestRouteTitle(this.router.routerState.snapshot),
          );
        });
      });
  }
}
